import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { deriveFmea, dimensionFmea, fmeaClassify, FMEA_BUCKETS, MATRIX_BG } from "@/lib/aepContent";

/**
 * FMEA risk visualization: shows the three 1-10 scales (Severidade × Ocorrência × Detecção)
 * with their RPN (Risk Priority Number, 1-1000) bucketed into Baixo/Moderado/Alto/Crítico.
 *
 * When `assessmentId` is provided, the per-dimension table becomes editable —
 * the technician can override the derived S/O/D individually. Overrides are
 * persisted to `assessment.fmea_overrides` via PUT /assessments/{aid}/fmea-overrides.
 */
export default function RiskMatrixFMEA({
  overallScore = 0,
  dimensions = [],
  criticalCount = 0,
  totalResponses = 0,
  expectedWorkers = 0,
  showLegend = true,
  showDimensionTable = true,
  assessmentId = null,
  initialOverrides = {},
  editable = false,
  strict = false,
}) {
  const [overrides, setOverrides] = useState(initialOverrides || {});

  useEffect(() => { setOverrides(initialOverrides || {}); }, [initialOverrides]);

  const worstScore = dimensions.length
    ? Math.max(...dimensions.filter((d) => d.responses > 0).map((d) => d.score), 0)
    : 0;
  const hasData = totalResponses > 0 && overallScore > 0;

  const overall = hasData
    ? deriveFmea({ overallScore, worstDimensionScore: worstScore,
                   criticalCount, totalResponses, expectedWorkers, strict })
    : null;

  const persist = async (next) => {
    if (!assessmentId) return;
    try {
      await api.put(`/assessments/${assessmentId}/fmea-overrides`, { overrides: next });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const updateOverride = (dim, key, valStr) => {
    const v = parseInt(valStr, 10);
    if (Number.isNaN(v) || v < 1 || v > 10) {
      toast.error("Valor deve ser um inteiro entre 1 e 10.");
      return;
    }
    const next = { ...overrides, [dim]: { ...(overrides[dim] || {}), [key]: v } };
    setOverrides(next);
    persist(next);
  };

  const clearOverride = (dim) => {
    const next = { ...overrides };
    delete next[dim];
    setOverrides(next);
    persist(next);
  };

  return (
    <div className="space-y-5" data-testid="risk-matrix-fmea">
      {/* Scale visualisation — three 1-10 bars side-by-side */}
      {hasData ? (
        <div className="grid md:grid-cols-3 gap-4">
          <FmeaScale label="Severidade (S)" hint="Intensidade do pior fator + respostas críticas." value={overall.S} testid="fmea-sev" />
          <FmeaScale label="Ocorrência (O)" hint="Frequência geral do risco no grupo avaliado." value={overall.O} testid="fmea-occ" />
          <FmeaScale label="Detecção (D)" hint="Capacidade de detectar pela empresa. Quanto MAIOR, PIOR." value={overall.D} testid="fmea-det" />
        </div>
      ) : (
        <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
          <i className="fa-solid fa-info-circle mr-2"></i>
          Aguardando respostas para calcular S × O × D.
        </div>
      )}

      {/* RPN result card */}
      {hasData && (
        <div className={`border-2 ${overall.bucket.cls} p-5 flex flex-col md:flex-row items-stretch md:items-center gap-4`}
             data-testid="fmea-rpn-card">
          <div className="flex-1">
            <div className="overline mb-1">Risk Priority Number · RPN</div>
            <div className="font-mono text-3xl md:text-4xl font-extrabold tracking-tight">
              {overall.S} × {overall.O} × {overall.D} = <span data-testid="fmea-rpn-value">{overall.rpn}</span>
              <span className="text-xs text-slate-500 font-sans ml-2">/ 1.000</span>
            </div>
            <p className="text-xs mt-2 leading-relaxed">{overall.bucket.description}</p>
          </div>
          <div className="text-center md:text-right">
            <div className="overline mb-1">Classificação</div>
            <div className={`${overall.bucket.cls} border-2 border-current px-4 py-2 text-lg font-display font-extrabold uppercase tracking-wider inline-block`}
                 data-testid="fmea-classification">
              {overall.bucket.label}
            </div>
          </div>
        </div>
      )}

      {/* Per-dimension RPN breakdown table */}
      {hasData && showDimensionTable && dimensions.length > 0 && (
        <div className="card border-slate-200" data-testid="fmea-dimension-table">
          <div className="flex justify-between items-baseline mb-3">
            <div className="overline">RPN por dimensão psicossocial</div>
            {editable && (
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                <i className="fa-solid fa-pen-to-square mr-1"></i> Editável · valores 1-10
              </span>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 font-semibold uppercase text-xs">Dimensão</th>
                <th className="font-semibold uppercase text-xs text-center w-20">S</th>
                <th className="font-semibold uppercase text-xs text-center w-20">O</th>
                <th className="font-semibold uppercase text-xs text-center w-20">D</th>
                <th className="font-semibold uppercase text-xs text-right">RPN</th>
                <th className="font-semibold uppercase text-xs text-right">Classe</th>
                {editable && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {dimensions
                .filter((d) => d.responses > 0)
                .sort((a, b) => b.score - a.score)
                .map((d) => {
                  const derived = dimensionFmea(d, { criticalCount, totalResponses, expectedWorkers, strict });
                  const ov = overrides[d.dimensao] || {};
                  const S = ov.S ?? derived.S;
                  const O = ov.O ?? derived.O;
                  const D = ov.D ?? derived.D;
                  const rpn = S * O * D;
                  const bucket = fmeaClassify(rpn);
                  const hasOv = Boolean(ov.S || ov.O || ov.D);
                  return (
                    <tr key={d.dimensao} className="border-b border-slate-100"
                        data-testid={`fmea-row-${d.dimensao}`}>
                      <td className="py-2.5">
                        {d.dimensao}
                        {hasOv && (
                          <span className="ml-2 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5">
                            <i className="fa-solid fa-pen mr-1"></i>Ajustado
                          </span>
                        )}
                      </td>
                      <EditableCell editable={editable} value={S} onChange={(v) => updateOverride(d.dimensao, "S", v)} testid={`fmea-${d.dimensao}-S`} />
                      <EditableCell editable={editable} value={O} onChange={(v) => updateOverride(d.dimensao, "O", v)} testid={`fmea-${d.dimensao}-O`} />
                      <EditableCell editable={editable} value={D} onChange={(v) => updateOverride(d.dimensao, "D", v)} testid={`fmea-${d.dimensao}-D`} />
                      <td className="text-right font-mono font-bold">{rpn}</td>
                      <td className="text-right">
                        <span className={`${bucket.cls} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
                          {bucket.label}
                        </span>
                      </td>
                      {editable && (
                        <td className="text-right">
                          {hasOv && (
                            <button onClick={() => clearOverride(d.dimensao)}
                                    title="Restaurar valores derivados"
                                    data-testid={`fmea-${d.dimensao}-reset`}
                                    className="text-slate-400 hover:text-rose-600">
                              <i className="fa-solid fa-rotate-left text-xs"></i>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {showLegend && (
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <span className="overline">Legenda RPN:</span>
          {FMEA_BUCKETS.map((b, i) => {
            const prev = i === 0 ? 1 : FMEA_BUCKETS[i - 1].max + 1;
            return (
              <span key={b.label} className={`${MATRIX_BG[b.label]} border px-3 py-1 font-bold uppercase tracking-wider`}>
                {b.label} <span className="font-mono opacity-70 ml-1">{prev}–{b.max === 1000 ? "1000" : b.max}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditableCell({ editable, value, onChange, testid }) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => { setLocal(String(value)); }, [value]);
  if (!editable) {
    return <td className="text-center font-mono">{value}</td>;
  }
  const commit = () => {
    if (local !== String(value) && local !== "") onChange(local);
  };
  return (
    <td className="text-center">
      <input
        type="number" min="1" max="10" value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        data-testid={testid}
        className="w-14 text-center font-mono font-bold border border-slate-300 px-1 py-1 focus:border-emerald-700 focus:outline-none"
      />
    </td>
  );
}

function FmeaScale({ label, hint, value, testid }) {
  const pct = Math.max(5, (value / 10) * 100);
  const toneClass = value <= 3 ? "bg-emerald-600"
                  : value <= 6 ? "bg-amber-500"
                              : "bg-rose-600";
  return (
    <div className="card border-slate-200" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="overline">{label}</div>
        <div className="font-mono text-2xl font-extrabold text-slate-900">{value}<span className="text-xs text-slate-400">/10</span></div>
      </div>
      <div className="h-2 bg-slate-100 mb-2 overflow-hidden">
        <div className={`h-full transition-all ${toneClass}`} style={{ width: `${pct}%` }}></div>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">{hint}</p>
    </div>
  );
}

